import { Component, ElementRef, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { Site } from 'app/classes/site';
import { APIService } from 'app/shared/api.service';
import { SiteService } from 'app/shared/site.service';
import { WindowService } from 'app/shared/window.service';
import { IRagCounts } from './telemetry-list-legend/telemetry-list-legend.component';
import { AssetService } from 'app/shared/asset.service';
import { ISensorTypeItem } from 'app/layout/data-table/data-table-filter-types/data-table-filter-types.component';
import { IGetOrgTelemetryResponse, OrgService } from 'app/shared/org.service';

@Component({
  selector: 'app-telemetry-list',
  templateUrl: './telemetry-list.component.html',
  styleUrls: ['./telemetry-list.component.css'],
  standalone: false
})
export class TelemetryListComponent implements OnInit, OnDestroy {
  readonly LOCALSTORAGE_KEY = 'telemetry:config';
  sites = signal<Site[]>(null);
  filterCount = signal<number>(null);
  defaultSite: Site = null;
  selectedAsset = signal<any>(null);
  results: any[];
  isMobile: boolean;
  breadcrumb = signal<any[]>([]);
  isLoadingSiteOrAsset: boolean;
  isLoadingSites = signal<boolean>(false);
  isLoadingAssetsForSite: boolean;
  filterResults: any[];
  searchFor: string;
  gatewaysHidden = [];
  gatewaysHiddenAsObject = {};
  ragCounts = signal<IRagCounts>(null);
  view = signal<'list' | 'card'>('card');
  multipleAssets = [];
  isAdmin = signal<boolean>(false);

  selectedSensor = signal<any>(null);
  tableFilter = signal<any>(null);
  firstLoad = true;

  sensorTypes = signal<ISensorTypeItem[]>([]);
  sensorTypeSelected = signal<ISensorTypeItem>(null);
  // Component must have gateways to be useful
  noSitesWithGateways = signal<boolean>(null);

  @ViewChild('top', { static: true }) contentPage: ElementRef;

  constructor(private siteService: SiteService, private apiService: APIService, private windowService: WindowService, private assetService: AssetService, private orgService: OrgService) {
    this.getLocalstorageConfig();
    this.isAdmin.set(this.apiService.isAdmin());
    this.isMobile = this.windowService.isMobile();
  }

  sensorTypeChange(sensorType: ISensorTypeItem) {
    this.sensorTypeSelected.set(sensorType);
    this.filter();
  }

  setLocalstorageConfig() {
    const config = { view: this.view() };
    localStorage.setItem(this.LOCALSTORAGE_KEY, JSON.stringify(config));
  }

  telemetryTableAssetSelected(asset) {
    this.showTelemetry(asset);
  }

  getLocalstorageConfig() {
    const configString = localStorage.getItem(this.LOCALSTORAGE_KEY);
    if (!configString) {
      return;
    }
    const config = JSON.parse(configString);
    if (config.view) {
      this.view.set(config.view);
    }
  }

  startSelectingAssets() {
    this.apiService.toastSuccess('Future use', 'in development');
  }

  ngOnInit(): void {

    this.isLoadingSiteOrAsset = true;
    this.isLoadingSites.set(true);
    this.loadGatewaysHidden();
    this.siteService.getSites().then(sites => {
      this.sites.set(sites.filter(s => s.gatewayCount));
      if (this.sites().length === 0) {
        this.noSitesWithGateways.set(true);
      }
      this.isLoadingSites.set(false);
      if (!this.isMobile) {
        this.setDefaultSite(this.loadDefualtSite());
      }
      this.isLoadingSiteOrAsset = false;
      if (this.firstLoad && this.defaultSite) {
        // On first load scroll site into view
        setTimeout(() => {
          try {
            document.getElementById('tl-site-' + this.defaultSite.id).scrollIntoView({ block: "center" });
          } catch (e) { }
        }, 100);

      }
      this.firstLoad = false;
    });
  }

  setDefaultSite(site: Site) {
    this.defaultSite = site;
    if (!this.defaultSite && this.sites?.length > 0) {
      this.defaultSite = this.sites[0];
    }
    this.loadTelemetryForSite();
  }

  gatewayHeaderClick(index: number) {
    const gatewayId = this.filterResults[index][0].gatewayId;
    const isHidden = this.gatewaysHidden.some(id => id === gatewayId);

    if (isHidden) {
      this.gatewaysHidden = this.gatewaysHidden.filter(id => id !== gatewayId);
    } else {
      this.gatewaysHidden.push(gatewayId);
    }
    this.gatewaysHiddenBuildHelpers();
    this.saveGatewaysHidden();
  }

  gatewaysHiddenBuildHelpers() {
    this.gatewaysHiddenAsObject = {};
    this.gatewaysHidden.forEach(id => {
      this.gatewaysHiddenAsObject[id] = true;
    });
  }

  searchChanged(searchText) {
    this.searchFor = searchText.toLowerCase();
    this.filter();
  }

  ragSelected(rag) {
    if (this.searchFor === rag) {
      this.searchFor = '';
    } else {
      this.searchFor = rag;
    }
    this.filter();
  }

  filter() {
    const ragCounts: IRagCounts = { red: 0, amber: 0, green: 0, none: 0 };

    this.results.forEach(g => g.forEach((a: IGetOrgTelemetryResponse) => {
      ragCounts[a.rag || 'none']++;
    }));

    this.ragCounts.set(ragCounts);

    if ((!this.searchFor || this.searchFor.length < 1) && !this.sensorTypeSelected()) {
      this.filterResults = this.results;
      this.filterCount.set(this.results.length);
      return;
    }

    this.filterResults = [];

    for (let index = 0; index < this.results.length; index++) {
      const gateway = this.results[index];

      const exists = gateway.filter((asset: IGetOrgTelemetryResponse) => {

        if (this.sensorTypeSelected()) {
          if (asset.assetType !== this.sensorTypeSelected().id) {
            return false;
          }

          if (!this.searchFor || this.searchFor.length < 1) {
            // nothing to search for so return true
            return true;
          }
        }

        if (asset.title.toLowerCase().includes(this.searchFor)) {
          return true;
        }
        if (String(asset.assetId) === this.searchFor) {
          return true;
        }

        if (asset.gatewayTitle.toLowerCase().includes(this.searchFor)) {
          return true;
        }

        if (asset.rag && asset.rag.includes(this.searchFor)) {
          return true;
        }

        if (!asset.rag && this.searchFor === 'none') {
          return true;
        }

        if (asset.gatewayId === this.searchFor) {
          return true;
        }

        return false;
      });
      if (exists.length) {
        this.filterResults.push(exists);
      }
    }

    this.filterCount.set(this.filterResults.length);
    this.ragCounts.set(ragCounts);
  }

  saveDefaultSite(site: Site) {
    const payload = site.serialise();
    localStorage.setItem(this.apiService.getUserOrg().id + ':telemetry:site', JSON.stringify(payload));
  }

  saveGatewaysHidden() {
    localStorage.setItem(this.apiService.getUserOrg().id + ':telemetry:gateways', JSON.stringify({ hidden: this.gatewaysHidden }));
  }

  loadDefualtSite(): Site | null {
    const data = localStorage.getItem(this.apiService.getUserOrg().id + ':telemetry:site');

    if (data) {
      const site = new Site(JSON.parse(data));

      return site;
    } else {
      if (this.sites()?.length > 0) {
        return this.sites()[0];
      }
    }

    return null;
  }

  loadGatewaysHidden() {
    try {
      const data = localStorage.getItem(this.apiService.getUserOrg().id + ':telemetry:gateways');
      if (data) {
        this.gatewaysHidden = JSON.parse(data).hidden;
        this.gatewaysHiddenBuildHelpers();
      }
    } catch (e) {
      console.log(e);
    }
  }

  assetLoaded(event: any) {
    this.isLoadingSiteOrAsset = false;
  }

  backToSites() {
    this.defaultSite = null;
    this.selectedAsset.set(null);
    this.results = null;
    this.isLoadingSiteOrAsset = null;
  }

  siteClick(site: Site) {
    this.selectedAsset.set(null);
    this.isLoadingSiteOrAsset = null;
    this.saveDefaultSite(site);
    this.setDefaultSite(site);


    this.contentPage.nativeElement.scrollTo(0, 0);
  }

  crumbClick(index?: number) {
    if ((index === undefined || this.breadcrumb()?.length === 1) && this.isMobile) {
      this.backToSites();
    }

    switch (index) {
      case 0:
        this.closeAsset();
        break;
    }
  }

  loadTelemetryForSite() {
    if (!this.defaultSite) {
      return;
    }
    this.isLoadingSiteOrAsset = true;
    this.isLoadingAssetsForSite = true;
    this.breadcrumb.set([{ label: this.defaultSite.title }]);


    this.orgService.getTelemetry({ site: { id: this.defaultSite.id } }).then(result => {
      let gateways = [];
      if (result.length === 0) {
        this.results = [];
        this.filterResults = [];
        this.sensorTypes.set([]);
        this.isLoadingSiteOrAsset = this.isLoadingAssetsForSite = false;

        return;
      }
      result = result.filter(asset => asset.assetType !== 32 && asset.assetType !== 21 && asset.assetType !== 31 && asset.assetType !== 12);

      result.forEach(asset => asset.assetTypeTitle = this.assetService.getTypeTitle(asset.assetType));

      // Allocate gateways their own array location
      for (let idx = 0; idx < result.length; idx++) {
        let gatewayId = result[idx].gatewayId;
        if (idx === 0 || result[idx - 1].gatewayId !== gatewayId) {
          gateways.push([]);
        }
        gateways[gateways.length - 1].push(result[idx]);
      }

      this.results = gateways.reverse();
      this.updateSensorTypesUsed();
      this.isLoadingSiteOrAsset = this.isLoadingAssetsForSite = false;
      this.filter();

    });
  }

  updateSensorTypesUsed() {
    const rows = this.results.flat();

    const sensorsUsed: ISensorTypeItem[] = [...rows.reduce((u, c) => {
      // Skip null for new asset types
      if (c.assetType) {
        if (!u.has(c.assetType)) {
          u.set(c.assetType, { title: c.assetTypeTitle, id: c.assetType, count: 1, isSelected: false });
        } else {
          u.get(c.assetType).count++;
        }
      }
      return u;
    }, new Map()).values()];

    this.sensorTypes.set(sensorsUsed.sort((a, b) => a.title > b.title ? 1 : -1));
  }

  showTelemetry(asset: IGetOrgTelemetryResponse) {
    this.isLoadingSiteOrAsset = true;
    this.selectedAsset.set(asset);
    this.breadcrumb().push({ label: asset.title });
    try {
      if (!asset.assetId) {
        console.error('NO ASSET / ID');
        return;
      }
      this.apiService.postRoute(`/telemetry/${asset.assetId}`);
    } catch (e) {
      this.isLoadingSiteOrAsset = false;
      console.log(e);
    }
  }

  closeAsset() {
    this.selectedAsset.set(null);
    this.isLoadingSiteOrAsset = null;
    this.breadcrumb().pop();
  }

  ngOnDestroy(): void {
    this.setLocalstorageConfig();
  }
}

